// src/components/AdminHeader.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface User {
  id: number;
  name: string;
  email: string;
  isAdmin: boolean;
}

interface AdminHeaderProps {
  user?: User;
  apiUrl: string;
  variant?: "sidebar" | "header";
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ user, apiUrl, variant = "header" }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const res = await fetch(`${apiUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // Navigation buttons
  const navButtons = (
    <ul className={variant === "sidebar" ? "flex flex-col gap-2" : "flex flex-col md:flex-row md:gap-4 px-4 py-3 md:p-0"}>
      {[
        { name: "Dashboard", path: "/dashboard" },
        { name: "Categories", path: "/categories" },
        { name: "Product Types", path: "/product-types" },
        { name: "Products", path: "/products" },
        { name: "Orders", path: "/orders" },
        { name: "Reviews", path: "/reviews" },
      ].map((btn) => (
        <li key={btn.name}>
          <button
            onClick={() => navigate(btn.path)}
            className={`w-full text-left px-4 py-2 rounded-lg font-medium transition-colors duration-200
              ${variant === "sidebar"
                ? "text-gray-700 hover:bg-gray-100"
                : "text-gray-700 hover:text-indigo-600 hover:bg-gray-50 md:hover:bg-transparent"
              }`}
          >
            {btn.name.toUpperCase()}
          </button>
        </li>
      ))}
      <li>
        <button
          onClick={handleLogout}
          className={`w-full text-left px-4 py-2 rounded-lg font-medium text-red-600 transition-colors duration-200
            ${variant === "sidebar"
              ? "hover:bg-red-50"
              : "hover:text-red-700 hover:bg-red-50 md:hover:bg-transparent"
            }`}
        >
          Logout
        </button>
      </li>
    </ul>
  );

  // Sidebar variant
  if (variant === "sidebar") {
    return (
      <div className="h-full flex flex-col p-6 bg-gray-50 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="h-12 w-auto" />
          {user && (
            <span className="text-gray-800 font-medium text-sm hidden lg:inline">
              Welcome, <span className="font-semibold">{user.name}</span>
            </span>
          )}
        </div>
        <nav className="flex-1">{navButtons}</nav>
      </div>
    );
  }

  // Header (mobile/top) variant
  return (
    <header className="bg-white shadow-md border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 md:px-8">
        <div className="flex items-center gap-3">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="h-10 w-auto" />
          {user && <span className="hidden md:inline text-gray-700 font-medium">{user.name}</span>}
        </div>

        {/* Hamburger for mobile */}
        <div className="md:hidden">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-2xl text-gray-700 p-2 rounded-md hover:bg-gray-100 transition"
          >
            ☰
          </button>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex md:items-center md:gap-3">{navButtons}</nav>
      </div>

      {/* Mobile dropdown */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 border-t border-gray-200 bg-white ${
          menuOpen ? "max-h-screen" : "max-h-0"
        }`}
      >
        {navButtons}
      </div>
    </header>
  );
};

export default AdminHeader;
